import humanizeDuration, { Unit } from 'humanize-duration'
import parse from 'parse-duration'
import i18n from '../plugins/i18n'
import momentTimezone from 'moment-timezone'
import { DateTime } from 'luxon'
import { useSettingsStore } from '@/store/settings'
const { t } = i18n.global

const intlOptionsWithSeconds: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
}

const intlOptionsWithoutSeconds: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
}

function pad(n: number) {
  return String(n).padStart(2, '0')
}

function formatScientific(date: Date, withSeconds = true): string {
  const datePart = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  const timePart = withSeconds
    ? `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    : `${pad(date.getHours())}:${pad(date.getMinutes())}`

  return `${datePart}, ${timePart}`
}
/**
 * Formats a timestamp according to the user locale settings.
 * Supports optional seconds and custom 'scientific' format.
 */
function humanReadableTimestamp(timestamp: number, withSeconds = true): string {
  if (timestamp === undefined || timestamp === null) {
    return t('invalid_timestamp')
  }
  const settingsStore = useSettingsStore()
  const date = new Date(timestamp)

  if (settingsStore.dateLocale === 'scientific') {
    return formatScientific(date, withSeconds)
  }

  const options = withSeconds ? intlOptionsWithSeconds : intlOptionsWithoutSeconds
  return new Intl.DateTimeFormat(settingsStore.dateLocale, options).format(date)
}

/**
 * Calculates the duration between the start timestamp and end timestamp and returns a human-readable string representation of the duration.
 *
 * @param {number} start - The start timestamp.
 * @param {number} [end] - The end timestamp. If not provided, the duration will be calculated from the start timestamp to the current time.
 * @param {Array<string>} [units] - The units to include in the duration string. Defaults to ['y', 'mo', 'd', 'h', 'm', 's'].
 * @param {boolean} [round] - Whether to round the duration values. Defaults to false.
 * @param {number} [maxDecimalPoints] - The maximum number of decimal points to include in the duration values.
 * @returns {string} - The human-readable string representation of the duration.
 */
function humanReadableDuration(
  start: number,
  end: number | undefined = undefined,
  units: Unit[] | undefined = undefined,
  round: boolean = false,
  maxDecimalPoints: number | undefined = undefined
): string {
  let duration = 0
  if (end === undefined) duration = start
  else duration = end - start
  if (!units) units = ['y', 'mo', 'd', 'h', 'm', 's']
  return humanizeDuration(duration, { units, round, language: i18n.global.locale.value, maxDecimalPoints })
}

function humanReadableDurationInDays(start: number, end: number | undefined = undefined): string {
  let duration = 0
  if (end === undefined) duration = start
  else duration = end - start
  return humanizeDuration(duration, { units: ['d'], language: i18n.global.locale.value, round: true })
}

function humanReadableDurationInHoursAndMinutes(start: number, end: number | undefined = undefined): string {
  let duration = 0
  if (end === undefined) duration = start
  else duration = end - start
  return humanizeDuration(duration, { units: ['h', 'm'], language: i18n.global.locale.value, round: true })
}

function parseDuration(duration: string): number {
  const result = parse(duration)
  if (result === null) throw new Error(`Invalid duration string: ${duration}`)
  return result
}

function getAvailableTimezones() {
  return momentTimezone.tz.names()
}

function getCurrentTimezone() {
  return momentTimezone.tz.guess()
}

/**
 * Calculates the number of days between two given timestamps.
 * If the end date is not provided, the current date is used.
 * This calculation is based on the Luxon library,
 * which is safer when dealing with edge cases like daylight saving time.
 *
 * @param {number} start - The timestamp of the start date.
 * @param {number} end - The timestamp of the end date.
 * @returns {number} The number of days between the start and end dates (rounded down to the nearest whole number).
 */
function getDurationInDaysFromTimestamp(start: number, end: number = Date.now()): number {
  const startDate = new Date(start).setHours(0, 0, 0, 0)
  const days = DateTime.fromMillis(end).diff(DateTime.fromMillis(startDate.valueOf()), 'days').toObject().days
  if (days === undefined) throw new Error('Could not calculate duration in days from timestamps')
  return Math.floor(days)
}

export {
  humanReadableDurationInHoursAndMinutes,
  humanReadableTimestamp,
  humanReadableDuration,
  parseDuration,
  getAvailableTimezones,
  getCurrentTimezone,
  humanReadableDurationInDays,
  getDurationInDaysFromTimestamp,
}
